import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Currency } from "../../utils";
import { BalanceType, MaxWin } from "../../commander/Types";
import initialState from "../initial-state";

const details = createSlice({
  name: "details",
  initialState: initialState.details,
  reducers: {
    showBars(state) {
      return { ...state, visible: true };
    },

    hideBars(state) {
      return { ...state, visible: false };
    },

    freeze(state) {
      return { ...state, frozen: true };
    },

    unfreeze(state) {
      return { ...state, frozen: false };
    },

    setLoaded(state, action: PayloadAction<boolean>) {
      return { ...state, loaded: action.payload };
    },

    setCurrency(state, action: PayloadAction<Currency>) {
      if (
        typeof action.payload.symbol === "undefined" ||
        typeof action.payload.isoCode === "undefined"
      ) {
        console.warn("cannot set currency:", action.payload);
        return state;
      }

      return { ...state, currency: action.payload };
    },

    initBalance(
      state,
      action: PayloadAction<{ amount: string; type: BalanceType }>
    ) {
      const { amount, type } = action.payload;

      return { ...state, balance: { value: amount, net: "0.000", type } };
    },
    setBalance(
      state,
      action: PayloadAction<{
        value: string;
        win: string;
        fullUpdate?: boolean;
      }>
    ) {
      const { value, win, fullUpdate } = action.payload;
      // if the update is not "full" the net balance stays the same
      const net = fullUpdate
        ? (parseFloat(state.balance.net) + parseFloat(win)).toFixed(3)
        : state.balance.net;

      return {
        ...state,
        balance: { ...state.balance, net, value },
      };
    },

    // used the decrease the balance on spin start, i.e. to subtract the stake /from the balance/ on spin
    decreaseBalance(
      state,
      action: PayloadAction<{
        value: string;
        next: string;
        fullUpdate: boolean;
      }>
    ) {
      // next is the balance that will be set after the spin is over /visually/
      // and it is used to ensure that there is a balance returned from the server
      const { value, next, fullUpdate } = action.payload;
      const amount = parseFloat(value);
      const oldValue = parseFloat(state.balance.value);
      const newValue = oldValue - amount;
      const net = fullUpdate
        ? (parseFloat(state.balance.net) - amount).toFixed(3)
        : state.balance.net;

      if (
        !value ||
        !next ||
        isNaN(newValue) ||
        isNaN(amount) ||
        isNaN(oldValue) ||
        amount > oldValue ||
        newValue > oldValue
      ) {
        return state;
      }

      return {
        ...state,
        balance: { ...state.balance, net, value: newValue.toFixed(3) },
      };
    },

    setGameName(state, action: PayloadAction<string>) {
      return { ...state, gameName: action.payload };
    },

    toggleFullscreen(state, action: PayloadAction<boolean>) {
      return { ...state, fullscreen: action.payload };
    },

    toggleMute(state, action: PayloadAction<boolean>) {
      return { ...state, sound: { ...state.sound, mute: action.payload } };
    },

    setSoundLevel(state, action: PayloadAction<number>) {
      return {
        ...state,
        sound: {
          ...state.sound,
          level: action.payload,
          mute: action.payload === 0,
        },
      };
    },

    toggleLogo(state, action: PayloadAction<boolean>) {
      return { ...state, logo: action.payload };
    },

    setLobby(state, action: PayloadAction<string>) {
      return { ...state, lobby: action.payload };
    },

    setBank(state, action: PayloadAction<string>) {
      return { ...state, bank: action.payload };
    },

    setAccountUri(state, action: PayloadAction<string>) {
      return { ...state, accountUri: action.payload };
    },

    startTimer(state, action: PayloadAction<number>) {
      return { ...state, timer: action.payload };
    },

    toggleHistory(state, action: PayloadAction<boolean>) {
      return { ...state, history: action.payload };
    },

    toggleTurboSpin(state, action: PayloadAction<boolean | undefined>) {
      const value =
        typeof action.payload === "boolean" ? action.payload : !state.turbo;
      return { ...state, turbo: value };
    },

    setMaxWin(state, action: PayloadAction<boolean | MaxWin | undefined>) {
      let maxWin;
      if (typeof action.payload === "number") {
        maxWin = action.payload;
      } else {
        maxWin = !!action.payload ? MaxWin.Germany : MaxWin.Normal;
      }

      return { ...state, maxWin };
    },

    setDynamicPaytable(state, action: PayloadAction<boolean>) {

      return { ...state, dynamicPaytable: action.payload };
    },

    setRTP(state, action: PayloadAction<{ value?: number; level?: number }>) {
      const value = action.payload?.value?.toString() ?? "";
      const level = action.payload?.level ?? -1;

      return { ...state, rtp: { level, value } };
    },

    setSlamStop(state, action: PayloadAction<boolean>) {
      return { ...state, slamStop: action.payload };
    },

    setLanguage(state, action: PayloadAction<string>) {
      // assumes that the language is valid - the validation is done by the game-manager
      return { ...state, language: action.payload };
    },

    setVersion(state, action: PayloadAction<string>) {
      return { ...state, serverVersion: action.payload };
    },
  },
});

export default details;

export const {
  showBars,
  hideBars,
  initBalance,
  decreaseBalance,
  freeze,
  unfreeze,
  toggleMute,
  toggleFullscreen,
  toggleLogo,
  toggleTurboSpin,
  toggleHistory,
  setCurrency,
  setBalance,
  setSoundLevel,
  setGameName,
  setLanguage,
  setLobby,
  setBank,
  setAccountUri,
  setLoaded,
  setRTP,
  setMaxWin,
  setDynamicPaytable,
  setSlamStop,
  setVersion,
  startTimer,
} = details.actions;
